import { ref, inject, provide, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ToastOptions, ToastInternal } from './toast.types'
import { getApiErrorMessage } from '../errors'

const TOAST_KEY = Symbol('toast')

export interface ToastApi {
  show: (options: ToastOptions) => void
  showApiError: <T>(error: T) => void
}

export const toasts: Ref<ToastInternal[]> = ref([])

function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

function show(options: ToastOptions): void {
  const id = options.id ?? generateId()
  const toast: ToastInternal = {
    ...options,
    id,
    duration: options.duration ?? 3000,
    position: options.position ?? 'top-right',
  }
  toasts.value.push(toast)
  setTimeout(() => {
    remove(id)
  }, toast.duration)
}

function remove(id: string): void {
  const idx = toasts.value.findIndex((t) => t.id === id)
  if (idx !== -1) {
    toasts.value.splice(idx, 1)
  }
}

export function useToast(): ToastApi {
  const api = inject<ToastApi>(TOAST_KEY)
  if (!api) throw new Error('ToastProvider is missing. Wrap your app with ToastProvider.')
  return api
}

export function provideToast(): void {
  const i18n = useI18n()

  function showApiError<T>(error: T): void {
    const message = getApiErrorMessage(i18n, error)
    show({ message, type: 'error' })
  }

  provide(TOAST_KEY, { show, showApiError })
}
